import { uuidv4 } from '@/shared/utilities';

export enum AttachmentType {
  DOCUMENT = 'DOCUMENT',
  AUDIO = 'AUDIO',
  LINK = 'LINK',
  GIF = 'GIF',
  LOCATION = 'LOCATION',
  STICKER = 'STICKER',
  MEDIA = 'MEDIA',
  FILE = 'FILE'
}

export enum MediaType {
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO'
} /*  */

export class Attachment {
  contentType: AttachmentType = AttachmentType.FILE;
  url?: string;
  filename?: string;
  mimeType?: string;
  size?: number;

  id?: string = '';
  file?: File;
  blobUrl?: string; // For preview before upload

  waveforms?: number[];

  constructor(file: File, waveforms?: number[]) {
    this.id = uuidv4();
    this.file = file;
    this.filename = file.name;
    this.size = file.size;
    this.mimeType = file.type;
    this.blobUrl = URL.createObjectURL(file);
    this.waveforms = waveforms;
  }
}

export class AudioAttachment extends Attachment {
  durationMs?: number;

  constructor(file: File, object?: Partial<AudioAttachment>) {
    super(file);
    this.contentType = AttachmentType.AUDIO;
    this.durationMs = object?.durationMs || 0;
    this.url = object?.url || '';
    this.waveforms = object?.waveforms;
  }
}

export class FileAttachment extends Attachment {
  constructor(file: File, object?: Partial<MediaAttachment>) {
    super(file);
    this.contentType = AttachmentType.FILE;
    this.url = object?.url || '';
  }
}

export class GifAttachment extends Attachment {
  thumbnailUrl: Nullable<string> = '';
  width?: number = 0;
  height?: number = 0;
  blurHash: Nullable<string> = '';

  constructor(file: File, object?: Partial<MediaAttachment>) {
    super(file);
    this.contentType = AttachmentType.GIF;
    this.url = object?.url || '';
    this.blurHash = object?.blurHash || '';
    this.height = object?.height || 0;
    this.width = object?.width || 0;
  }
}

export class MediaAttachment extends Attachment {
  mediaType: MediaType = MediaType.IMAGE;
  thumbnailUrl: Nullable<string> = '';
  width?: number = 0;
  height?: number = 0;
  durationMs?: number;
  blurHash: Nullable<string> = '';

  constructor(file: File, object?: Partial<MediaAttachment>) {
    super(file);
    this.contentType = AttachmentType.MEDIA;
    this.durationMs = object?.durationMs || 0;
    this.blurHash = object?.blurHash || '';
    this.width = object?.width || 0;
    this.height = object?.height || 0;
    this.durationMs = object?.durationMs || 0;
    this.thumbnailUrl = object?.thumbnailUrl || '';
    this.url = object?.url || '';

    if (file.type.startsWith('video/')) {
      this.mediaType = MediaType.VIDEO;
    } else this.mediaType = MediaType.IMAGE;
  }
}
export class LinkAttachment extends Attachment {
  imageUrl: Nullable<string> = '';
  description: Nullable<string> = '';

  constructor(url: string, object?: Partial<LinkAttachment>);
  constructor(file: File, object?: Partial<LinkAttachment>);
  constructor(urlOrFile: string | File, object?: Partial<LinkAttachment>) {
    // Create a dummy file for the base constructor if URL is provided
    const file = typeof urlOrFile === 'string' ? new File([''], 'link', { type: 'text/plain' }) : urlOrFile;

    super(file);
    this.contentType = AttachmentType.LINK;

    // If URL is provided, set it directly
    if (typeof urlOrFile === 'string') {
      this.url = urlOrFile;
    }

    this.imageUrl = object?.imageUrl || '';
    this.description = object?.description || '';
    this.filename = object?.filename || this.extractDomainFromUrl(this.url || '');
  }

  private extractDomainFromUrl(url: string): string {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace('www.', '');
    } catch {
      return 'Unknown site';
    }
  }
}
